import config from "@/config";
import { getSiteContext } from "@/api/siteContext";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string
  ) {
    super(`API error ${status}: ${body}`);
    this.name = "ApiError";
  }
}

// Listener invoked when the cookie-based session is rejected
// (401 that a refresh couldn't recover). Wired up by AuthProvider so
// it can redirect to /login without creating a circular import here.
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

// Coalesce concurrent refreshes — if three parallel requests all 401,
// we refresh once and let all three retry off that single refresh.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/api/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: { "X-Requested-With": "fetch" },
        });
        return res.ok;
      } catch {
        return false;
      } finally {
        // Clear after microtask flush so awaiters all see the same promise.
        queueMicrotask(() => {
          refreshInFlight = null;
        });
      }
    })();
  }
  return refreshInFlight;
}

async function doFetch(path: string, options: RequestInit): Promise<Response> {
  const url = `${config.apiBaseUrl}${path}`;
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // CSRF sentinel — backend requires X-Requested-With: fetch on every
  // protected route. Simple-form CSRF can't set custom headers.
  headers["X-Requested-With"] = "fetch";

  // Optional site context — pages that "live in a site" register
  // themselves via setSiteContext so every audit event emitted by
  // calls they trigger gets a payload.site_id.
  const siteContext = getSiteContext();
  if (siteContext && !headers["X-Site-Context"]) {
    headers["X-Site-Context"] = siteContext;
  }

  // Don't set Content-Type for FormData — browser handles it.
  if (!(options.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  return fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  let res: Response;
  try {
    res = await doFetch(path, options);
  } catch {
    throw new Error("Network error — please check your connection and try again.");
  }

  // On 401 try a one-shot refresh + retry. Skip for auth endpoints to
  // avoid looping if refresh itself returns 401.
  if (res.status === 401 && !path.startsWith("/api/auth/")) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      try {
        res = await doFetch(path, options);
      } catch {
        throw new Error("Network error — please check your connection and try again.");
      }
    }
  }

  if (res.status === 401) {
    unauthorizedHandler?.();
  }

  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body);
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json() as Promise<T>;
  }
  return res.text() as unknown as T;
}
